// AWS Organization connections (#914, slice 1 of #792): what discovery found under each,
// read off `routes/aws_organizations.py`. The external id and the report secret appear in
// no answer read here: only a start's launch parameters carry them.

import type { Api } from "./http";

export const ORGANIZATIONS_PATH = "/api/v1/aws-organizations";

/** Per-account membership evidence, as the last complete discovery recorded it. */
export interface Membership {
  /** Whether the last complete `ListAccounts` listed the account. */
  listed: boolean;
  in_scope: boolean;
  /** AWS's own account state: ACTIVE, SUSPENDED, PENDING_CLOSURE, CLOSED, PENDING_ACTIVATION. */
  account_state: string;
  ancestry: string[];
  generation: number;
  /** A later discovery could not finish reading this: the values are the last good ones. */
  unknown: boolean;
}

export interface Verification {
  state: "unverified" | "verified" | "failed";
  /** The member role exactly as the member reported it, suffix and all. */
  reported_role_arn: string | null;
  verified_at: string | null;
  error: string | null;
}

export interface RegionStatus {
  region: string;
  /** The regional StackSet's instance status there, or NOT_DEPLOYED. */
  status: string;
  unknown: boolean;
}

export interface Candidate {
  account_id: string;
  name: string;
  generation: number;
  membership: Membership | null;
  verification: Verification;
  regions: RegionStatus[];
  /** The workspace's manually connected account for the same id, left as it is. */
  manual_cloud_account_id: string | null;
}

export interface OrganizationConnection {
  id: string;
  organization_id: string;
  management_account_id: string;
  monitoring_account_id: string | null;
  administration_mode: string;
  transport_mode: string | null;
  scope: { entire_organization: boolean; ou_ids: string[] };
  home_region: string;
  regions: string[];
  discovery_role_arn: string | null;
  discovery_reported_at: string | null;
  member_stack_set_name: string;
  regional_stack_set_name: string;
  generation: number;
  last_attempt_at: string | null;
  /** The last discovery in which every page of every read succeeded. */
  last_complete_at: string | null;
  last_error: string | null;
  created_at: string;
  candidates: Candidate[];
}

export interface OrganizationsRead {
  connections: OrganizationConnection[];
}

export const readOrganizations = (api: Api): Promise<OrganizationsRead> =>
  api.get<OrganizationsRead>(ORGANIZATIONS_PATH);

/** Run discovery again now; the answer is the connection as it stands after it. */
export const discoverAgain = (api: Api, connectionId: string): Promise<OrganizationConnection> =>
  api.post<OrganizationConnection>(`${ORGANIZATIONS_PATH}/${encodeURIComponent(connectionId)}/discover`);
